import { useId, type CSSProperties, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import {
  MetricAction,
  isMetricActionable,
  type MetricActionProps,
} from "./metric-action";
import "./summary-metrics.css";

export type SummaryMetric = MetricActionProps & {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  icon?: ReactNode;
  tone?: "blue" | "teal" | "amber" | "coral" | "neutral";
};

/** A single metric pattern for page summaries and compact card summaries. */
export function SummaryMetrics({
  items,
  density = "default",
  label = "요약 정보",
  columns,
}: {
  items: SummaryMetric[];
  density?: "default" | "compact";
  label?: string;
  columns?: 1 | 2 | 3 | 4;
}) {
  const id = useId();
  return (
    <dl
      className={`summary-metrics summary-metrics--${density}`}
      aria-label={label}
      style={
        {
          "--summary-columns":
            columns ?? Math.max(1, Math.min(items.length, 4)),
        } as CSSProperties
      }
    >
      {items.map((item, index) => {
        const actionable = isMetricActionable(item);
        const valueId = `${id}-${index}-value`;
        const noteId =
          item.note !== null &&
          item.note !== undefined &&
          item.note !== "" &&
          item.note !== false
            ? `${id}-${index}-note`
            : undefined;
        return (
          <div
            className={`summary-metric tone-${item.tone ?? "blue"}${actionable ? " metric-actionable" : ""}`}
            key={item.label}
          >
            <dt>
              {item.icon && (
                <span className="summary-metric-icon" aria-hidden="true">
                  {item.icon}
                </span>
              )}
              <span>{item.label}</span>
              {actionable && (
                <ChevronRight
                  className="metric-action-arrow"
                  size={16}
                  aria-hidden="true"
                />
              )}
              <MetricAction
                href={item.href}
                onSelect={item.onSelect}
                actionLabel={item.actionLabel}
                disabled={item.disabled}
                label={item.label}
                descriptionId={[valueId, noteId].filter(Boolean).join(" ")}
              />
            </dt>
            <dd>
              <strong id={valueId}>{item.value}</strong>
              {noteId && (
                <span id={noteId} className="summary-metric-note">
                  {item.note}
                </span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
